export const STAFF_MODULE_CODES = [
  'dashboard',
  'catalog',
  'sales',
  'payments',
  'storefront',
  'settings',
  'pos',
] as const

export type StaffModuleCode = (typeof STAFF_MODULE_CODES)[number]

export const STAFF_MODULES: { code: StaffModuleCode; label: string; description: string }[] = [
  { code: 'dashboard', label: 'Dashboard', description: 'Overview and quick actions' },
  { code: 'catalog', label: 'Catalog', description: 'Categories, products, and special offers' },
  { code: 'sales', label: 'Sales', description: 'Orders, customers, analytics, discount codes, and transactions' },
  { code: 'payments', label: 'Payments', description: 'Wallet, gateways, checkout, and withdrawals' },
  { code: 'storefront', label: 'Storefront', description: 'Store profile, homepage, newsletter, and messages' },
  { code: 'settings', label: 'Settings', description: 'General store configuration' },
  { code: 'pos', label: 'In-store POS', description: 'Make sales and sales history' },
]

export function staffModuleLabel(code: string): string {
  return STAFF_MODULES.find((m) => m.code === code)?.label ?? code
}

/** First navigable path for staff based on assigned modules (sidebar order). */
export function firstStaffModulePath(modules: string[] | undefined | null): string {
  const granted = new Set(modules ?? [])
  const order: { path: string; module: StaffModuleCode }[] = [
    { path: '/', module: 'dashboard' },
    { path: '/categories', module: 'catalog' },
    { path: '/orders', module: 'sales' },
    { path: '/commerce', module: 'payments' },
    { path: '/storefront', module: 'storefront' },
    { path: '/settings', module: 'settings' },
    { path: '/pos/sales', module: 'pos' },
  ]
  for (const entry of order) {
    if (granted.has(entry.module)) return entry.path
  }
  return '/'
}

export function pathnameStaffModule(pathname: string): StaffModuleCode | null {
  if (pathname === '/' || pathname === '') return 'dashboard'
  if (pathname.startsWith('/categories') || pathname.startsWith('/products') || pathname.startsWith('/special-offers')) {
    return 'catalog'
  }
  if (pathname.startsWith('/orders') || pathname.startsWith('/customers') || pathname.startsWith('/discount-codes') || pathname.startsWith('/transactions') || pathname.startsWith('/analytics')) {
    return 'sales'
  }
  if (pathname.startsWith('/commerce') || pathname.startsWith('/withdrawals') || pathname.startsWith('/pickup-locations')) {
    return 'payments'
  }
  if (
    pathname.startsWith('/storefront') ||
    pathname.startsWith('/homepage-sections') ||
    pathname.startsWith('/newsletter-subscribers') ||
    pathname.startsWith('/contact-messages')
  ) {
    return 'storefront'
  }
  if (pathname.startsWith('/settings')) return 'settings'
  if (pathname.startsWith('/pos')) return 'pos'
  if (pathname.startsWith('/team')) return null
  return null
}

export function staffCanAccessPath(
  pathname: string,
  role: string | undefined,
  modules: string[] | undefined | null,
): boolean {
  if (role === 'org_admin') return true
  if (role !== 'staff') return true
  const required = pathnameStaffModule(pathname)
  if (!required) return true
  return (modules ?? []).includes(required)
}

export function staffHasModule(modules: string[] | undefined | null, module: StaffModuleCode): boolean {
  return (modules ?? []).includes(module)
}

export function staffHasAnyModule(
  modules: string[] | undefined | null,
  codes: StaffModuleCode[],
): boolean {
  const granted = new Set(modules ?? [])
  return codes.some((code) => granted.has(code))
}

/** Storefront AI routes require catalog or storefront module (org_admin bypasses on API). */
export function canUseStorefrontAi(role: string | undefined, modules: string[] | undefined | null): boolean {
  if (role === 'org_admin') return true
  return staffHasAnyModule(modules, ['catalog', 'storefront'])
}
